
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = "https://hajguvculrsuylksuylq.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imhhamd1dmN1bHJzdXlsa3N1eWxxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQwMDgxODAsImV4cCI6MjA1OTU4NDE4MH0.Wp25x8Vr0kgtxVEnk0wnHk2mE8HPTCbn39vIqi_nQIU";

// Ensure proper persistence and session management
export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storage: typeof localStorage !== 'undefined' ? localStorage : undefined
  }
});

// Storage helpers - CORRIGIDO
export const ensureStorageBuckets = async () => {
  try {
    // Check if the buckets exist
    const { data: buckets, error } = await supabase.storage.listBuckets();
    
    if (error) {
      console.error("Error listing buckets:", error);
      return false;
    }
    
    const avatarsBucketExists = buckets.some(bucket => bucket.name === 'avatars');
    const clientFilesBucketExists = buckets.some(bucket => bucket.name === 'client-files');
    const attachmentsBucketExists = buckets.some(bucket => bucket.name === 'attachments');
    const processAttachmentsBucketExists = buckets.some(bucket => bucket.name === 'process-attachments');
    
    // Create missing buckets
    if (!avatarsBucketExists) {
      const { error: createError } = await supabase.storage.createBucket('avatars', {
        public: true,
        allowedMimeTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'],
        fileSizeLimit: 5 * 1024 * 1024 // 5MB
      });
      
      if (createError) {
        console.error("Error creating avatars bucket:", createError);
      } else {
        console.log("Bucket 'avatars' created successfully");
      }
    }
    
    if (!clientFilesBucketExists) {
      const { error: createError } = await supabase.storage.createBucket('client-files', {
        public: true,
        allowedMimeTypes: ['image/*', 'application/pdf', 'text/plain'],
        fileSizeLimit: 50 * 1024 * 1024 // 50MB
      });
      
      if (createError) {
        console.error("Error creating client-files bucket:", createError);
      } else {
        console.log("Bucket 'client-files' created successfully");
      }
    }
    
    if (!attachmentsBucketExists) {
      const { error: createError } = await supabase.storage.createBucket('attachments', {
        public: true,
        allowedMimeTypes: ['image/*', 'application/pdf', 'text/plain', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        fileSizeLimit: 20 * 1024 * 1024 // 20MB
      });
      
      if (createError) {
        console.error("Error creating attachments bucket:", createError);
      } else {
        console.log("Bucket 'attachments' created successfully");
      }
    }
    
    if (!processAttachmentsBucketExists) {
      const { error: createError } = await supabase.storage.createBucket('process-attachments', {
        public: true,
        allowedMimeTypes: [
          'image/*', 
          'application/pdf', 
          'text/plain', 
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/msword',
          'application/vnd.ms-excel'
        ],
        fileSizeLimit: 50 * 1024 * 1024 // 50MB
      });
      
      if (createError) {
        console.error("Error creating process-attachments bucket:", createError);
      } else {
        console.log("Bucket 'process-attachments' created successfully");
      }
    }
    
    return true;
  } catch (error) {
    console.error("Error checking/creating buckets:", error);
    return false;
  }
};

// File upload functions
export const uploadProfileImage = async (file: File, userId: string) => {
  try {
    const fileExt = file.name.split('.').pop();
    const filePath = `${userId}/${Date.now()}.${fileExt}`;
    
    const { data, error } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, {
        upsert: true,
        cacheControl: '3600'
      });
      
    if (error) throw error;
    
    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);
      
    return publicUrl;
  } catch (error) {
    console.error("Error uploading profile image:", error);
    throw error;
  }
};

export const uploadCardAttachment = async (file: File, cardId: string) => {
  try {
    console.log(`Starting upload for card ${cardId}, file: ${file.name}`);
    
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `card_${cardId}/${fileName}`;
    
    console.log(`Uploading to path: ${filePath}`);
    
    // Add retry logic for more reliable uploads
    let retries = 0;
    const maxRetries = 2;
    let uploadSuccess = false;
    let uploadError = null;
    let data = null;
    
    while (retries <= maxRetries && !uploadSuccess) {
      try {
        const result = await supabase.storage
          .from('attachments')
          .upload(filePath, file, {
            upsert: true,
            cacheControl: '3600'
          });
        
        if (result.error) {
          console.error(`Upload attempt ${retries + 1} failed:`, result.error);
          uploadError = result.error;
          retries++;
          
          if (retries <= maxRetries) {
            console.log(`Retrying (attempt ${retries + 1}/${maxRetries + 1})...`);
            await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry
          }
        } else {
          data = result.data;
          uploadSuccess = true;
          break;
        }
      } catch (err) {
        console.error(`Exception in upload attempt ${retries + 1}:`, err);
        uploadError = err;
        retries++;
        
        if (retries <= maxRetries) {
          console.log(`Retrying (attempt ${retries + 1}/${maxRetries + 1})...`);
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry
        }
      }
    }
    
    if (!uploadSuccess) {
      throw uploadError || new Error("Upload failed after multiple attempts");
    }
      
    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('attachments')
      .getPublicUrl(filePath);
      
    console.log(`Upload successful. Public URL: ${publicUrl}`);
    
    return {
      url: publicUrl,
      path: filePath
    };
  } catch (error: any) {
    console.error("Error uploading attachment:", error);
    throw error instanceof Error ? error : new Error(`Upload error: ${String(error)}`);
  }
};

// User management functions

export const updatePassword = async (password: string) => {
  const { error } = await supabase.auth.updateUser({
    password
  });
  
  if (error) throw error;
  
  return true;
};

export const updateUserProfile = async (userData: {
  name?: string;
  agency_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  avatar_url?: string;
}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) throw new Error("User is not authenticated");
    
    console.log("Updating user profile for:", user.id, userData);
    
    // Update users table
    const { data, error } = await supabase
      .from('users')
      .update({
        name: userData.name,
        agency_name: userData.agency_name,
        email: userData.email,
        phone: userData.phone,
        address: userData.address,
        avatar_url: userData.avatar_url,
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id);
    
    if (error) {
      console.error("Error updating user profile:", error);
      throw error;
    }
    
    console.log("Profile updated successfully:", data);
    
    // Synchronize with auth email if needed
    if (userData.email && userData.email !== user.email) {
      const { error: authError } = await supabase.auth.updateUser({
        email: userData.email
      });
      
      if (authError) {
        console.error("Error updating auth email:", authError);
        throw authError;
      }
    }
    
    return true;
  } catch (error) {
    console.error("Error updating user profile:", error);
    throw error;
  }
};

export const updateUserTheme = async (theme: 'light' | 'dark' | 'system') => {
  try {
    // Always update localStorage for immediate effect
    localStorage.setItem('theme', theme);
    
    // Apply theme to document immediately
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (theme === 'light') {
      document.documentElement.classList.remove('dark');
    } else if (theme === 'system') {
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.classList.toggle('dark', systemPrefersDark);
    }
    
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.log("No authenticated user, theme saved to localStorage only");
      return true; // Still succeed if just saving to localStorage
    }
    
    const { error } = await supabase
      .from('users')
      .update({ theme })
      .eq('id', user.id);
    
    if (error) {
      console.error("Database error when updating theme:", error);
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error("Error in updateUserTheme:", error);
    throw error;
  }
};

export const updateUserNotifications = async (notifications: {
  email: boolean;
  push: boolean;
  mentions: boolean;
  tasks: boolean;
  approvals: boolean;
}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) throw new Error("User is not authenticated");
    
    const { error } = await supabase
      .from('users')
      .update({ notifications })
      .eq('id', user.id);
    
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error("Error updating notification settings:", error);
    throw error;
  }
};

// Team management functions

export const sendTeamInvite = async (email: string, role: string, permissions: Record<string, boolean>) => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) throw new Error("User is not authenticated");
  
  const { error } = await supabase
    .from('team_invites')
    .insert({
      inviter_id: user.id,
      email,
      role,
      permissions
    });
  
  if (error) throw error;
  
  return true;
};

export const listTeamInvites = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) throw new Error("User is not authenticated");
  
  const { data, error } = await supabase
    .from('team_invites')
    .select('*')
    .eq('inviter_id', user.id);
  
  if (error) throw error;
  
  return data;
};

export const acceptTeamInvite = async (inviteId: string) => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) throw new Error("User is not authenticated");
  
  // Get the invite
  const { data: invite, error: fetchError } = await supabase
    .from('team_invites')
    .select('*')
    .eq('id', inviteId)
    .eq('email', user.email)
    .single();
  
  if (fetchError || !invite) throw new Error("Invite not found");
  
  // Update invite
  const { error: updateError } = await supabase
    .from('team_invites')
    .update({
      accepted: true,
      accepted_at: new Date().toISOString()
    })
    .eq('id', inviteId);
  
  if (updateError) throw updateError;
  
  // Add as team member
  const { error: insertError } = await supabase
    .from('team_members')
    .insert({
      user_id: user.id,
      added_by: invite.inviter_id,
      role: invite.role,
      permissions: invite.permissions
    });
  
  if (insertError) throw insertError;
  
  return true;
};

export const listTeamMembers = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) throw new Error("User is not authenticated");
  
  const { data, error } = await supabase
    .from('team_members')
    .select('*')
    .eq('added_by', user.id);
  
  if (error) throw error;
  
  return data;
};

// Add a helper function to check if user is authenticated
export const isUserAuthenticated = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabase.auth.getSession();
    return !error && !!data.session;
  } catch (err) {
    console.error("Error checking authentication:", err);
    return false;
  }
};

// On module load, ensure storage buckets exist
ensureStorageBuckets().then(success => {
  if (success) {
    console.log("Storage buckets verified/created successfully");
  } else {
    console.error("Failed to verify/create storage buckets");
  }
});
